import cn from "classnames";
import { printFullName } from "~/helpers/user";
import { Avatar, AvatarFallback, AvatarImage } from "~/atoms/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";

interface Props {
  user: {
    firstName: string;
    lastName?: string;
    email?: string | null;
    imageUrl?: string | null;
  };
  size?: "xs" | "sm" | "md" | "lg";
  withName?: boolean;
  withEmail?: boolean;
  showTooltip?: boolean;
}

const sizeClasses = {
  xs: "size-6",
  sm: "size-8",
  md: "size-10",
  lg: "size-12",
};

const AvatarWithFallback = ({
  user,
  size = "md",
  withName = false,
  withEmail = false,
  showTooltip = false,
}: Props) => {
  const { firstName, lastName, email, imageUrl } = user;
  const shortName = `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`;

  const avatar = (
    <Avatar className={cn(sizeClasses[size])}>
      <AvatarImage src={imageUrl ?? undefined} alt={printFullName(user)} />
      <AvatarFallback className={cn(size === "xs" && "text-xs")}>
        {shortName.toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );

  const content =
    withName || withEmail ? (
      <div className="flex items-center gap-2">
        {avatar}
        <div className="flex min-w-0 flex-col">
          {withName && (
            <div className="line-clamp-1">{printFullName(user)}</div>
          )}
          {withEmail && email && (
            <div className="text-muted-foreground line-clamp-1 text-sm">
              {email}
            </div>
          )}
        </div>
      </div>
    ) : (
      avatar
    );

  if (!showTooltip) {
    return content;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent>{printFullName(user)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AvatarWithFallback;
